import React from "react";
import styled from "@emotion/styled";
import { EducationalArticleTipsModule } from "@domain/learn/learn-service";
import { ArticleRenderer } from "@ui/components/learn/article-renderer";
import { physioMeTabletBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";

interface TipsModuleSectionProps {
	tipsModule: EducationalArticleTipsModule;
}

export const TipsModuleSection = ({ tipsModule }: TipsModuleSectionProps): JSX.Element => (
	<TipsModuleContainer>
		<TipsIconWrapper>
			<TipsIcon src={tipsModule.image.url} alt={tipsModule.title || "Tips"} />
		</TipsIconWrapper>
		<TipsModuleBox>
			<TipsModuleTitle>{tipsModule.title}</TipsModuleTitle>
			<TipsModuleContent>
				<ArticleRenderer content={tipsModule.content} />
			</TipsModuleContent>
		</TipsModuleBox>
	</TipsModuleContainer>
);

// Tips Module Styles
const TipsModuleContainer = styled.div`
	position: relative;
	width: 100%;
	margin-top: 100px;
	margin-bottom: 32px;

	@media (max-width: 520px) {
		margin-top: 70px;
	}
`;

const TipsIconWrapper = styled.div`
	position: absolute;
	top: -60px;
	left: 50%;
	transform: translateX(-50%) rotate(45deg);
	width: 120px;
	height: 120px;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${colors.white};
	border-radius: 16px;
	box-shadow: -4.927px 0px 9.854px 0px rgba(38, 38, 48, 0.08);

	@media ${physioMeTabletBreakpoint} {
		width: 120px;
		height: 120px;
		top: -60px;
	}

	@media (max-width: 520px) {
		width: 100px;
		height: 100px;
		top: -50px;
	}
`;

const TipsIcon = styled.img`
	width: 100%;
	height: 100%;
	object-fit: contain;
	transform: rotate(-45deg) scale(1.2);
`;

const TipsModuleBox = styled.div`
	background: #ecedfc;
	border-radius: 16px;
	padding: 80px 32px 40px 32px;
	position: relative;
	z-index: 1;

	@media ${physioMeTabletBreakpoint} {
		padding: 80px 24px 32px 24px;
	}

	@media (max-width: 520px) {
		padding: 70px 20px 24px 20px;
	}
`;

const TipsModuleTitle = styled.h2`
	font-family: ${fonts.header};
	font-size: 24px;
	font-weight: 700;
	color: #212bc2;
	margin: 0 0 24px 0;
	line-height: 1;
	margin-top: 32px;
	@media (max-width: 520px) {
		font-size: 20px;
	}
`;

const TipsModuleContent = styled.div`
	font-family: ${fonts.body};
	color: ${colors.black};

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		margin-top: 18px;
		margin-bottom: 10px;
		font-family: ${fonts.header};
		font-size: 18px;
		font-weight: 700;
		color: ${colors.primaryDark50};
	}

	p {
		margin-top: 16px;
		margin-bottom: 16px;
		color: ${colors.black};
		font-size: 18px;
		line-height: 1.5;
		&:last-child {
			margin-bottom: 0;
		}
	}
	ul,
	ol {
		margin-top: 16px;
		margin-bottom: 16px;
		padding-left: 24px;
		font-size: 18px;
		color: ${colors.black};
		line-height: 1.5;
	}

	li {
		margin-bottom: 8px;
		font-family: ${fonts.body};
		font-size: 18px;
		font-weight: 400;
		color: ${colors.black};

		&:last-child {
			margin-bottom: 0;
		}
	}

	ul li {
		list-style-type: none;
		position: relative;
		padding-left: 4px;

		&::before {
			content: "";
			position: absolute;
			left: -24px;
			top: 10px;
			width: 6px;
			height: 6px;
			background-color: ${colors.primary};
			border-radius: 50%;
		}
	}

	strong {
		font-family: ${fonts.header};
		font-weight: 700;
		color: ${colors.black};
	}
`;
